import React, { useCallback, useState, useEffect } from "react";
import {
  Button,
  Divider,
  P,
  Box,
  Small,
  Toggle,
  Flex,
  Select,
  H4,
} from "orcs-design-system";

import { get, isEqual, map, set } from "lodash";
import {
  useOrgFeatureFlagsForTenant,
  useOrgConfigForTenant,
} from "src/hooks/orgConfig";
import FancyCard, {
  FancyCardBody,
  FancyCardFooter,
} from "src/components/FancyCard/FancyCard";

import SlackConfigInput from "./SlackConfigInput";

const LOCALE_OPTIONS = [
  { value: "en-AU", label: "English (Australia) — DD/MM/YYYY" },
  { value: "en-GB", label: "English (United Kingdom) — DD/MM/YYYY" },
  { value: "en-US", label: "English (United States) — MM/DD/YYYY" },
];

const getOptionByValue = (options, value) =>
  options.find((o) => o.value === value) || null;

const LABELS = {
  individualContactOptions: "Individual contact config",
  phone: "Phone",
  email: "Email",
  slack: "Slack",
  microsoftTeams: "Microsoft Teams",
  googleChat: "Google Chat",
};

const CUSTOM_COMPONENTS = {
  slack: SlackConfigInput,
};

const getindividualContactOptions = (individualContactOptions) => {
  return {
    phone: get(individualContactOptions, "phone", true),
    email: get(individualContactOptions, "email", true),
    microsoftTeams: get(individualContactOptions, "microsoftTeams", false),
  };
};

const getInitialData = (featureFlags) => {
  return {
    individualContactOptions: getindividualContactOptions(
      featureFlags?.individualContactOptions
    ),
  };
};

const TenantFeatureFlags = () => {
  const { featureFlags, update, updating } = useOrgFeatureFlagsForTenant();
  const {
    config: tenantConfig,
    update: updateConfig,
    updating: updatingConfig,
  } = useOrgConfigForTenant();
  const [locale, setLocale] = useState(tenantConfig?.locale || "en-AU");

  useEffect(() => {
    if (tenantConfig?.locale) {
      setLocale(tenantConfig.locale);
    }
  }, [tenantConfig?.locale]);

  const flags = getInitialData(featureFlags ?? {});
  const [newFeatureFlags, setNewFeatureFlags] = useState(flags);

  // Update local state when tenant feature flags change
  useEffect(() => {
    if (featureFlags) {
      setNewFeatureFlags(getInitialData(featureFlags));
    }
  }, [featureFlags]);

  const onFlagChange = useCallback(
    (key, value) => setNewFeatureFlags({ ...set(newFeatureFlags, key, value) }),
    [newFeatureFlags]
  );

  const onFlagToggle = useCallback(
    (key) => (evt) => onFlagChange(key, evt.target.checked),
    [onFlagChange]
  );

  const handleOnSave = useCallback(async () => {
    await update({ ...(featureFlags ?? {}), ...newFeatureFlags });
  }, [featureFlags, newFeatureFlags, update]);

  const handleOnReset = useCallback(() => {
    setNewFeatureFlags(flags);
  }, [flags]);

  const isChanged = !isEqual(newFeatureFlags, flags);

  return (
    <FancyCard height="100%">
      <FancyCardBody>
        <Box p="xs">
          <Flex flexDirection="column" gap="r">
            <P>Here you can set the tenant feature flags</P>
            <Small>
              Note: After making changes and saving, refresh the page to see
              changes in action.
            </Small>
            <Divider />
            <Box>
              <Flex flexDirection="column" gap="r">
                {map(newFeatureFlags, (value, key) => {
                  if (typeof value === "object") {
                    return (
                      <Box boxBorder="default" borderRadius={2} p="r">
                        <Flex flexDirection="column" gap="r">
                          <P>{LABELS[key]}</P>

                          {map(value, (nestedValue, nestedKey) => {
                            const flagKey = `${key}.${nestedKey}`;
                            const CustomComponent =
                              CUSTOM_COMPONENTS[nestedKey];

                            if (CustomComponent) {
                              return (
                                <CustomComponent
                                  value={nestedValue}
                                  onChange={(v) => onFlagChange(flagKey, v)}
                                />
                              );
                            }

                            return (
                              <Toggle
                                id={`${key}-${nestedKey}-Toggle`}
                                label={LABELS[nestedKey]}
                                checked={nestedValue}
                                onChange={onFlagToggle(flagKey)}
                              />
                            );
                          })}
                        </Flex>
                      </Box>
                    );
                  }

                  return (
                    <Toggle
                      id={`${key}-Toggle`}
                      label={LABELS[key]}
                      checked={value}
                      onChange={onFlagToggle(key)}
                    />
                  );
                })}
              </Flex>
            </Box>
          </Flex>
        </Box>
        <Divider />
        <Box mt="r">
          <Flex flexDirection="column" gap="r">
            <H4>Region &amp; Language</H4>
            <Select
              label="Default region / language"
              inputId="tenant-locale-select"
              options={LOCALE_OPTIONS}
              isSearchable={false}
              value={getOptionByValue(LOCALE_OPTIONS, locale)}
              onChange={(option) => {
                if (option) {
                  setLocale(option.value);
                }
              }}
              ariaLabel="Default region / language"
              isDisabled={updatingConfig}
            />
            <Flex>
              <Button
                onClick={() => updateConfig({ locale })}
                isLoading={updatingConfig}
                disabled={updatingConfig || locale === tenantConfig?.locale}
                variant={updatingConfig ? "disabled" : "success"}
              >
                Save region &amp; language
              </Button>
            </Flex>
          </Flex>
        </Box>
      </FancyCardBody>
      <FancyCardFooter>
        <Flex gap="s">
          <Button
            onClick={handleOnSave}
            isLoading={updating}
            disabled={updating || !isChanged}
            variant={updating ? "disabled" : "success"}
          >
            Save changes
          </Button>
          <Button
            onClick={handleOnReset}
            isLoading={updating}
            disabled={!isChanged}
            variant="ghost"
          >
            Reset changes
          </Button>
        </Flex>
      </FancyCardFooter>
    </FancyCard>
  );
};

export default TenantFeatureFlags;
